// TW Conversion

import { FunctionComponent } from 'react';
import CTAButton from '~/components/shared/CTAButton';
import TooltipV2 from '~/components/shared/TooltipV2';
import { handleSuperscripts, isEmpty } from '~/helper/editableHelpers';
import { useMonarchSlotContext } from '@redventures/cohesion-utils-react';
import { useVariantIds } from '~/lib/hooks/useVariantIds';

// Classes
import {
  getBgColor,
  getTextColor,
  wrapperClasses,
  headlineClasses,
  subheadlineClasses,
  tooltipClasses,
  ctaWrapperClasses,
  primaryCTAClasses,
} from './simplifiedSolidHeroClasses';

// Types and Default Props
import { DEFAULT_PROPS } from './data';

const SimplifiedSolidHero: FunctionComponent = () => {
  const { data } = useMonarchSlotContext<typeof DEFAULT_PROPS>();

  const { isDarkMode } = useVariantIds();

  const {
    variant,
    headline,
    subheadline,
    disclaimer,
    disclaimerLink,
    CTAButtonText,
    CTAButtonVariant,
    CTAButtonLink,
    hasSecondaryCTAButton,
    secondaryCTAButtonText,
    secondaryCTAButtonVariant,
    secondaryCTAButtonLink,
  } = data ?? DEFAULT_PROPS;

  const headlineWithSuperscript = handleSuperscripts(headline);
  const subheadlineWithSuperscript = handleSuperscripts(subheadline);
  const variantClasses = isDarkMode ? 'dark' : variant;

  // Classes
  const bgColorClasses = getBgColor(variantClasses);
  const textColorClasses = getTextColor(variantClasses);

  return (
    <div className={`${wrapperClasses} ${bgColorClasses}`}>
      <h1 className={`${headlineClasses} ${textColorClasses}`}>
        {headlineWithSuperscript}
      </h1>
      <p className={`${subheadlineClasses} ${textColorClasses}`}>
        <span className='items-center'>
          {subheadlineWithSuperscript}
          {!isEmpty(disclaimer) && (
            <TooltipV2
              text={disclaimer}
              link={disclaimerLink}
              className={tooltipClasses}
              elementPosition='SIMPLIFIED_SOLID_HERO'
            />
          )}
        </span>
      </p>
      <div className={ctaWrapperClasses}>
        <CTAButton
          elementPosition='HERO'
          elementName='SIMPLIFIED_SOLID_CTA'
          variant={CTAButtonVariant}
          elementType='SIGN_UP_CTA_PRIMARY'
          href={CTAButtonLink}
          className={primaryCTAClasses}
        >
          {CTAButtonText}
        </CTAButton>
        {hasSecondaryCTAButton && (
          <CTAButton
            elementPosition='HERO'
            elementName='SIMPLIFIED_SOLID_CTA'
            variant={secondaryCTAButtonVariant}
            elementType='SIGN_UP_CTA_SECONDARY'
            href={secondaryCTAButtonLink}
            className={
              secondaryCTAButtonVariant === 'text-link' ? textColorClasses : ''
            }
          >
            {secondaryCTAButtonText}
          </CTAButton>
        )}
      </div>
    </div>
  );
};

export default SimplifiedSolidHero;
